import Fuse from 'fuse.js'
import { useCallback, useEffect } from 'react'
import { SearchInput } from '../../SearchInput'
import { TextInputImperativeHandle } from '../../TextInputWithLabel'
import { useCascadingMenu, flattenItems } from '../Root'
import * as styles from '../styles'
import { useHandleItemKeyPress } from '../useHandleItemKeypress'

interface SearchProps {
  includeNestedOptions?: boolean
  placeholder?: string
}

export const Search: React.FC<SearchProps> = ({
  includeNestedOptions,
  placeholder,
}) => {
  const {
    focusedItemIndex,
    items,
    onActiveDescendentChange,
    setFocusedItemIndex,
    searchValue,
    setSearchResults,
    setSearchValue,
  } = useCascadingMenu()

  const onSearch = useCallback(
    (value) => {
      if (!value) return setSearchResults(items)

      const fuse = new Fuse(
        includeNestedOptions ? flattenItems(items) : items,
        {
          keys: ['label'],
          threshold: 0.3, // Good balance between accuracy and performance
        }
      )

      setSearchResults(fuse.search(value).map((r) => r.item))
    },
    [items, includeNestedOptions, setSearchResults]
  )

  const handleSearch = (value: string) => {
    setSearchValue(value)
    onSearch(value)
  }

  const { handleKeyPress, itemRef } = useHandleItemKeyPress<
    HTMLInputElement & TextInputImperativeHandle
  >({
    disabledKeys: ['ArrowRight', 'ArrowLeft', 'ArrowUp', 'Enter', ' '],
    hasChildren: false,
    index: -1,
    onDescendMenu: null,
    onSelect: null,
  })

  const onFocus = useCallback(() => {
    if (focusedItemIndex !== -1) {
      setFocusedItemIndex(-1)
    }
    onActiveDescendentChange?.(styles.SEARCH_ID)
  }, [onActiveDescendentChange, focusedItemIndex, setFocusedItemIndex])

  useEffect(() => {
    if (focusedItemIndex === -1) {
      itemRef.current?.focus()
    }
  }, [focusedItemIndex, itemRef])

  return (
    <SearchInput
      ref={itemRef}
      id={styles.SEARCH_ID}
      styleOverrides={{ inputContainer: styles.search }}
      placeholder={placeholder}
      value={searchValue}
      onFocus={onFocus}
      onKeyDown={handleKeyPress}
      onSearch={handleSearch}
    />
  )
}
Search.displayName = 'Lapidary: CascadingMenu.Search'
